'use client';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import { CardsType } from '../type';
import VideoTestimonial from './VideoTestimonial';
import { useEffect, useRef, useState } from 'react';
import type { MuxPlayerRefAttributes } from '@mux/mux-player-react';
import TextCard from './TextCard';
import ListContainer from './ListContainer';
import ItemContainer from './ItemContainer';
import useIsMobile from '../../../../utils/useIsMobile';
import DarkThemeProvider from '../../../../providers/DarkThemeProvider';
import LightThemeProvider from '../../../../providers/LightThemeProvider';
import { useTranslate } from '@jd/i18n';
import Optional from '../../../Optional';
import { useInView } from 'react-intersection-observer';

type Props = {
  data: CardsType;
  isMobile: boolean;
  infos: Record<
    string,
    { blurDataURL: string; aspectRatio: number; playbackId: string }
  >;
};

const Cards = ({ data, isMobile: initIsMobile, infos }: Props) => {
  const {
    cards,
    styling,
    cardType,
    alternate,
    orientation = 'vertical',
    role,
    filterByTags,
    pagination,
  } = data;
  const isMobile = useIsMobile(initIsMobile);
  const t = useTranslate('sections.cards');
  const videoRef = useRef<Map<string, MuxPlayerRefAttributes>>(new Map());
  const variant =
    cards.length < 4 || data.variant === 'grid-standard' ? 'static' : 'masonry';
  const ThemeProvider = styling.isDark ? DarkThemeProvider : LightThemeProvider;
  const isFlat = cardType === 'flat';

  const pageSize = isMobile ? pagination.mobile : pagination.desktop;
  const isPaginated = pageSize > 0;

  const initPageSize = initIsMobile ? pagination.mobile : pagination.desktop;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(
    initPageSize > 0 ? initPageSize : Infinity,
  );

  const allTags = filterByTags
    ? Array.from(new Set(cards.flatMap((card) => card.tags ?? [])))
    : [];

  const filteredCards =
    filterByTags && selectedTag
      ? cards.filter((card) => card.tags?.includes(selectedTag))
      : cards;

  const visibleCards = isPaginated
    ? filteredCards.slice(0, visibleCount)
    : filteredCards;
  const hasMore = isPaginated && visibleCount < filteredCards.length;

  const videos = cards
    .map((e) => e.video?.playbackId)
    .filter((e): e is string => !!e);

  const { ref: observerRef, inView } = useInView({
    threshold: isMobile ? 0.1 : 0.7,
  });

  useEffect(() => {
    const currentUrl = videos[currentIndex];
    const player = videoRef.current.get(currentUrl!);
    if (!player) return;

    if (inView) {
      player.play().catch((error) => {
        console.warn('Browser blocked autoplay:', error);
      });
    } else {
      player.pause();
    }
  }, [inView, currentIndex]);

  const handleEnded = () => {
    setCurrentIndex((prev) => prev + 1);
  };

  return (
    <ThemeProvider>
      <Box
        className={'tb-cards-container'}
        sx={{
          bgcolor: {
            md: isFlat ? 'background.paper' : 'inherit',
            xs: 'inherit',
          },
          px: { md: 4, xs: 0 },
          borderRadius: { xs: 0.5, md: 1 },
        }}
      >
        {filterByTags && allTags.length > 0 && (
          <Stack
            direction="row"
            sx={{ flexWrap: 'wrap', gap: 1, mb: 6 }}
            className={'tb-cards-filters'}
          >
            <Chip
              sx={{
                borderRadius: 0.5,
                py: 3,
                px: 1,
                fontSize: { md: 16, xs: 14 },
              }}
              label="All"
              onClick={() => setSelectedTag(null)}
              color={selectedTag === null ? 'secondary' : 'default'}
              variant={selectedTag === null ? 'filled' : 'outlined'}
            />
            {allTags.map((tag) => (
              <Chip
                sx={{
                  borderRadius: 0.5,
                  py: 3,
                  px: 1,
                  fontSize: { md: 16, xs: 14 },
                }}
                key={tag}
                label={tag}
                onClick={() =>
                  setSelectedTag((prev) => (prev === tag ? null : tag))
                }
                color={selectedTag === tag ? 'secondary' : 'default'}
                variant={selectedTag === tag ? 'filled' : 'outlined'}
              />
            ))}
          </Stack>
        )}
        <ListContainer
          variant={variant}
          initIsMobile={initIsMobile}
          sx={styling.listContainer.sx}
          columns={styling.listContainer.grid.columns[orientation]}
        >
          {visibleCards?.map((card, index) => {
            const playbackId = card.video?.playbackId;
            if (playbackId) {
              const key = playbackId + index;
              const info = infos[playbackId];
              return (
                <ItemContainer
                  ref={observerRef}
                  variant={variant}
                  key={key}
                  columns={styling.listContainer.grid.columns[orientation]}
                >
                  <VideoTestimonial
                    ref={(node) => {
                      if (node) {
                        videoRef.current.set(playbackId, node);
                      } else {
                        videoRef.current.delete(playbackId);
                      }
                    }}
                    variant={variant}
                    playbackId={playbackId}
                    description={card.description}
                    onEnd={handleEnded}
                    blurDataURL={info?.blurDataURL}
                    ar={info?.aspectRatio || card.video?.meta?.aspectRatio || 1}
                  />
                </ItemContainer>
              );
            }
            return (
              <ItemContainer
                variant={variant}
                key={card.id}
                columns={styling.listContainer.grid.columns[orientation]}
              >
                <TextCard
                  orientation={orientation}
                  variant={variant}
                  isMobile={isMobile}
                  moveAuthorTop={styling.listContainer.moveAuthorTop}
                  moveTagsTop={styling.listContainer.moveTagsTop}
                  {...card}
                  role={role}
                  flip={
                    alternate
                      ? index % 2
                        ? card.flip === 'all'
                          ? 'all'
                          : 'desktop'
                        : card.flip === 'all'
                          ? 'mobile'
                          : undefined
                      : card.flip
                  }
                  sx={styling.sx}
                  imageSx={styling.imageSx}
                />
              </ItemContainer>
            );
          })}
        </ListContainer>
        {isPaginated && (
          <Box
            sx={{
              mt: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Optional condition={filteredCards.length > pageSize}>
              <Typography variant="body2" color="text.secondary">
                {t('progress', {
                  visible: visibleCards.length,
                  total: filteredCards.length,
                })}
              </Typography>
            </Optional>
            {hasMore && (
              <Button
                variant="outlined"
                onClick={() => setVisibleCount((prev) => prev + pageSize)}
              >
                {t('loadMore')}
              </Button>
            )}
          </Box>
        )}
      </Box>
    </ThemeProvider>
  );
};

export default Cards;
